import { LoaderIcon } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { InvoiceDocuments, InvoiceItemsTable } from './components';
import {
  useCreatePaymentForInvoiceMutation,
  useInvoiceQuery,
} from './invoice.generated';
import {
  Button,
  InvoiceStatus,
  calculateInvoiceFees,
  fToCurrency,
  groupLineItemsByTaxRate,
  shouldHideInvoiceStatus,
  useParameters,
} from '@web/utils';
import arrowTop from '@web/utils/src/assets/icons/functions/arrow-top.svg';
import mollieLogo from '@web/utils/src/assets/icons/logos/mollie.png';
import { InvoiceStatuses } from '@web/utils/src/components/InvoiceStatus/InvoiceStatus';
import { useCustomToast } from '@/hooks/useCustomToast';

type InvoiceRouteParams = {
  retailerId: string;
  invoiceId: string;
};

const InvoiceDetailsPage: React.FC = () => {
  const { retailerId, invoiceId } = useParameters<InvoiceRouteParams>();
  const navigate = useNavigate();
  const toast = useCustomToast();
  const { t } = useTranslation();
  const [{ data }] = useInvoiceQuery({ variables: { invoiceId } });
  const invoice = data?.currentUser?.invoice;
  const [_, createPaymentInvoice] = useCreatePaymentForInvoiceMutation();

  const handleCreateInvoicePayment = async (
    invoiceId: string,
    paymentOptionKey: string,
  ) => {
    try {
      const response = await createPaymentInvoice({
        invoiceId: invoiceId,
        paymentOptionKey: paymentOptionKey,
      });

      if (response.data?.createPaymentForInvoice?.checkoutUrl) {
        window.open(
          response.data?.createPaymentForInvoice?.checkoutUrl,
          '_blank',
        );
      } else {
        toast('error', t('invoices.error_creating_invoice'));
      }
    } catch (error) {
      console.log(error);
      toast('error', t('invoices.error_creating_invoice'));
    }
  };

  const taxesByPercentage = groupLineItemsByTaxRate(invoice?.lineItems ?? []);

  if (!invoice) {
    return <LoaderIcon className="mx-auto mt-10-0" />;
  }

  return (
    <>
      <div className="flex items-center py-2-0 justify-between">
        <div className="flex items-center ">
          <div
            className="h-full flex items-center px-1-0 pl-0 cursor-pointer opacity-70 hover:opacity-90"
            onClick={() => navigate(`/documents/${retailerId}/invoices`)}
          >
            <ReactSVG src={arrowTop} className="text-white -rotate-90" />
          </div>
          <p className="text-2-0 font-bd">Rechnung: {invoice.id}</p>
        </div>
        {!shouldHideInvoiceStatus(retailerId) && (
          <InvoiceStatus status={invoice.status as InvoiceStatuses} t={t} />
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-x-4-0 pt-2-0r">
        <div>
          <InvoiceItemsTable invoice={invoice} />
        </div>
        <div id="order-left" className="text-body1">
          {invoice.document && (
            <InvoiceDocuments documents={[invoice.document]} />
          )}
          <div className="w-full my-3-0 text-body1 p-1-6 font-li flex flex-col gap-1-0">
            <div className="flex justify-between items-center">
              <span className="font-bd text-2-0">
                {t('invoices.total_net')}
              </span>
              <span className="font-bd text-2-0">
                {fToCurrency(invoice.totalNet || 0)}
              </span>
            </div>

            {taxesByPercentage &&
              Object.keys(taxesByPercentage).map((key, val) => (
                <div className="flex justify-between items-center" key={key}>
                  <span>{t('invoices.tax', { value: key })}</span>

                  <span>{fToCurrency(val || 0)}</span>
                </div>
              ))}

            <div className="flex justify-between mt-2-0 items-center">
              <span className="font-bd text-2-0">
                {t('invoices.total_gross')}
              </span>
              <span className="font-bd text-2-0">
                {fToCurrency(invoice.totalGross || 0)}
              </span>
            </div>
          </div>
          {invoice.status !== 'PAID' && invoice.paymentOptions.length > 0 && (
            <>
              <div>
                <div className="flex justify-between items-center mb-1-0">
                  <p className="font-bd">{t('invoices.buy_now_pay_later')}</p>
                  <img
                    src={mollieLogo}
                    alt="mollie-logo"
                    className="w-auto h-6"
                  />
                </div>
                <div className="w-full my-1-0 text-body1 p-1-6 rounded-8-0 bg-secondary-grey shadow-sm font-li">
                  <ul className="list-disc px-1-6">
                    <li>
                      {t('invoices.pay_in', {
                        value: '120',
                      })}
                    </li>
                    <li>
                      {t('invoices.pay_additional', {
                        value: calculateInvoiceFees(
                          invoice.totalGross || 0,
                          invoice.paymentOptions[0].fee,
                        ),
                      })}
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <Button
                  className="w-full"
                  onClick={() =>
                    handleCreateInvoicePayment(
                      invoice.id,
                      invoice.paymentOptions[0].id,
                    )
                  }
                >
                  {t('invoices.pay_later')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default InvoiceDetailsPage;
